import { take, map, isEmpty, get, find, cloneDeep } from 'lodash';
import connect from 'connect-alt';
import React, { Component, PropTypes } from 'react';
import { Row, Col } from 'react-bootstrap';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';
import { DataStoreDispatcher } from '../../lib/frankly';
import { formatDate } from '../../utils/DateUtils';
import DownArrow from '../shared/DownArrow';
import ModuleSubMenu from '../shared/ModuleSubMenu';
import { convertF2C } from '../../utils/WeatherUtils.js';
import translate from '../../utils/translate';

/**
 * Class representing the container for the multiday and hourly weather forecast components.
 *
 * Dependent on feed - wsi_endpoint
 */

@connect('Weather')
class DetailedWeatherBreakdown extends Component {
  static propTypes = {
    dayOptionValues: PropTypes.array,
    hiFirst: PropTypes.bool,
    expandFirstRowByDefault: PropTypes.bool,
    defaultForecastDisplayValue: PropTypes.string,
    showHourly: PropTypes.bool,
    showWeekend: PropTypes.bool,
    WeatherStore: PropTypes.object.isRequired,
    FRN_rawResponses: PropTypes.array
  };

  static contextTypes = {
    flux: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    config: PropTypes.object.isRequired,
  }

  static defaultProps = {
    hiFirst: false,
    expandFirstRowByDefault: false,
    showHourly: true,
    showWeekend: true,
    defaultForecastDisplayValue: 'hourly',
    dayOptionValues: ['3', '7', '10']
  };

  state = {
    temperatureFormat: 'F',
    isHourlyForecastDisplayed: (this.props.showHourly && this.props.defaultForecastDisplayValue === "hourly"),
    forecastDisplayValue:
      (this.props.defaultForecastDisplayValue === "hourly" && !this.props.showHourly) ||
      (this.props.defaultForecastDisplayValue === "weekend" && !this.props.showWeekend) ?
      this.props.dayOptionValues[0] :
        this.props.defaultForecastDisplayValue
  };

  componentWillMount() {
    const { flux } = this.context;

    flux.getActions('Weather').readWsi();

    this._weatherDataStoreDispatcher = new DataStoreDispatcher('franklyinc.com/weather');

    this._weatherDataStoreDispatcher.onChange('temperatureFormat', (val) => {
      this.setState({
        temperatureFormat: val
      });
    });
  }

  componentWillReceiveProps(nextProps) {
    const { isLoaded } = this.state;

    if (!isLoaded) {
      this._buildWeatherData(nextProps);
    }
  }

  componentWillUnmount() {
    this._weatherDataStoreDispatcher.off();
    this._weatherDataStoreDispatcher = null;
  }

  _setForecast(val, shouldDisplayHourlyForecast, event) {
    this.setState({
      isHourlyForecastDisplayed: shouldDisplayHourlyForecast,
      forecastDisplayValue: val
    });

    event.stopPropagation();
  }

  _getClassNames(defaultNames, dailyValue){
    let classNames = defaultNames;

    if (this.state.forecastDisplayValue === dailyValue) {
      classNames += " is-active";
    }

    return classNames;
  }

  _getWeatherFromEndpoint() {
    const { FRN_rawResponses } = this.props;

    if (isEmpty(FRN_rawResponses)) {
      return null;
    }

    const features = get(FRN_rawResponses, '[0].data.features', []);
    return find(features, { type: 'weather' });
  }

  _buildWeatherData(props) {
    const wsiData = get(props, 'WeatherStore.wsi.data');

    if (wsiData) {
      const newWsiData = cloneDeep(wsiData);

      const { dayOptionValues } = this.props;
      const dailyForecasts = get(newWsiData, 'Cities.City.DailyForecast.Day', []);

      let customDailyOptionValues = cloneDeep(dayOptionValues);

      const feedData = this._getWeatherFromEndpoint();

      // replace feed weather data to wsi weather data
      if (!isEmpty(feedData) && feedData['overrideprimarycityforecast'] === false) {
        // check current city with primary city
        const primaryCityName = get(feedData, 'weatherconditions.citys[0].cityname', '');
        const currentCity = get(this.context, 'location.city', '');

        if (currentCity.toLowerCase() === primaryCityName.toLowerCase()) {
          const forecasts = feedData.forecasts;

          // make wsi and feed start on the same day
          // sometime wsi is delayed and has one day behind the feed
          const wsiFirstDate = new Date(dailyForecasts[0]['@ValidDateLocal']).getDate();
          const feedFirstDate = new Date(forecasts[0]['datetime']).getDate();

          if (wsiFirstDate < feedFirstDate) {
            dailyForecasts.splice(0, 1);
          }

          // modify display days
          const displayDays = feedData.displaydays;
          customDailyOptionValues = dayOptionValues.filter(day => parseInt(day) < displayDays);
          customDailyOptionValues.push(displayDays.toString());
          dailyForecasts.splice(displayDays);

          // modify high and low data
          dailyForecasts.forEach((day, index) => {
            const { high, low, description } = forecasts[index] || {};

            day['@HiTempF'] = high || day['@HiTempF'];
            day['@LoTempF'] = low || day['@LoTempF'];
            day['@SkyText'] = description || day['@SkyText'];
            day['@HiTempC'] = high ? convertF2C(high) : day['@HiTempC'];
            day['@LoTempC'] = low ? convertF2C(low) : day['@LoTempC'];
          });

          const { flux } = this.context;
          flux.getActions('Weather').updateWsiData(newWsiData);
        }
      }

      this.setState({ isLoaded: true, customDailyOptionValues });
    }
  }

  render() {
    const {
      config: {
        appOptions: {
          language = 'en'
        } = {}
      } = {}
    } = this.context;

    const {
      WeatherStore: {
        wsi: {
          data: {
            Cities: {
              City: {
                DailyForecast: {
                  Day: dailyForecasts = []
                } = {},
                HourlyForecast: {
                  Hour: hourlyForecasts = []
                } = {}
              } = {}
            } = {}
          } = {}
        } = {}
      },
      hiFirst,
      showHourly,
      showWeekend,
      expandFirstRowByDefault
    } = this.props;

    const {
      temperatureFormat,
      isHourlyForecastDisplayed,
      isLoaded,
      forecastDisplayValue: daysToDisplay,
      customDailyOptionValues
    } = this.state;

    const translatedDay = translate('Day', language);
    const translatedDays = translate('Days', language);

    if (!isLoaded){
      return null;
    }

    if (!showHourly && !showWeekend && !customDailyOptionValues.length) {
      return null;
    }

    return (
      <div className="DetailedWeatherBreakdown">
        <ModuleSubMenu className="DetailedWeatherBreakdown-forecastOptions">
          {showHourly ?
            <li className={this._getClassNames("forecast-option", "hourly")}>
              <a onClick={this._setForecast.bind(this, "hourly", true)}>{translate('Hourly', language)}</a>
            </li> : null }
          {
            map(customDailyOptionValues, (val) => (
              <li key={val} className={this._getClassNames('forecast-option', val)}>
                <a onClick={this._setForecast.bind(this, val, false)}>
                  { // Add 's' suffix for only Spanish, WEB-5188
                    (language === 'es') &&
                    ((val > 1) ? `${val} ${translatedDays}` : `${val} ${translatedDay}`)
                  }
                  {
                    (language === 'en') && `${val} ${translatedDay}`
                  }
                </a>
              </li>
            ))
          }
          {showWeekend ?
            <li className={this._getClassNames("forecast-option", "weekend")}>
              <a onClick={this._setForecast.bind(this, "weekend", false)}>{translate('Weekend', language)}</a>
            </li> : null }
        </ModuleSubMenu>
        {isHourlyForecastDisplayed ?
          <HourlyDayForecast
            lang={language}
            forecasts={hourlyForecasts}
            temperatureFormat={temperatureFormat}
          /> :
          <MultiDayForecast
            lang={language}
            forecasts={dailyForecasts}
            daysToDisplay={daysToDisplay}
            temperatureFormat={temperatureFormat}
            hiFirst={hiFirst}
            expandFirstRowByDefault={expandFirstRowByDefault}
          />}
      </div>
    );
  }

}

/**
 * Class representing the hourly weather forecast component.
 */
class HourlyDayForecast extends Component {
  static propTypes = {
    forecasts: PropTypes.array.isRequired,
    temperatureFormat: PropTypes.string,
    lang: PropTypes.string,
  };

  render() {
    return (
      <div>
        <HourlyForecastHeadings lang={this.props.lang}/>
        <HourlyForecastBody {...this.props} />
      </div>
    );
  }
}

/**
 * Class representing the headings panel for the hourly weather forecast component.
 */
class HourlyForecastHeadings extends Component {
  render() {
    const { lang = 'en' } = this.props;
    return (
      <Row className="weather-multiday-hourly-headings">
        <Col xs={3} sm={3} md={2} className="multiday-hourly-heading">{translate(lang, 'Time')}</Col>
        <Col xs={2} sm={2} className="multiday-hourly-heading">Temp</Col>
        <Col xs={2} md={4} className="multiday-hourly-heading">
          <span className="long-name">{translate('Description', lang)}</span>
          <span className="short-name">Desc</span>
        </Col>
        <Col xs={2} sm={2} lg={1} className="multiday-hourly-heading">Precip</Col>
        <Col xs={2} sm={2} lg={1} className="multiday-hourly-heading">
          <span className="long-name">{translate('Humidity', lang)}</span>
          <span className="short-name">{translate('Humid', lang)}</span>
        </Col>
        <Col xsHidden smHidden mdHidden lg={2} className="multiday-hourly-heading">{translate('Wind', lang)}</Col>
      </Row>
    );
  }
}

/**
 * Class representing the container for the content within the hourly weather forecast component.
 */
class HourlyForecastBody extends Component {
  static propTypes = {
    forecasts: PropTypes.array.isRequired,
    temperatureFormat: PropTypes.string,
    lang: PropTypes.string,
  };

  state = {
    totalHoursToDisplay: 12
  };

  _showNextEightHours() {
    this.setState({
      totalHoursToDisplay: this.state.totalHoursToDisplay + 12
    });
  }

  render() {
    const {
      temperatureFormat,
      forecasts,
      lang = 'en',
    } = this.props;
    const {
      totalHoursToDisplay
    } = this.state;

    let forecastComponents;
    if (forecasts) {
      forecastComponents = map(
        take(forecasts, totalHoursToDisplay),
        (forecast, i) => {
          return (
            <HourlyForecastRow
              lang={lang}
              key={i}
              forecast={forecast}
              temperatureFormat={temperatureFormat}
              />
          );
        }
      );
    }

    return (
      <div>
        {forecastComponents}
        {totalHoursToDisplay < forecasts.length ?
          (
            <Row className="next-12-hours-row">
              <span onClick={() => this._showNextEightHours()} className="next-12-hours-label">{translate('Next 12 Hours', lang)}</span>
            </Row>
          )
          : null
        }
      </div>
    );
  }
}

/**
 * Class representing a row that contains information about a single hour within the hourly weather forecast component.
 */
class HourlyForecastRow extends Component {
  static propTypes = {
    forecast: PropTypes.object.isRequired,
    temperatureFormat: PropTypes.string,
    lang: PropTypes.string,
  };

  render() {
    const {
      forecast: {
        '@ValidDateLocal': dateTimeOfForecast,
        '@TempF': TempF,
        '@TempC': TempC,
        '@IconCode': iconCode,
        '@SkyMedium': descriptionText,
        '@PrecipChance': precipitation,
        '@RelHumidity': humidity,
        '@WndSpdMph': windSpeed,
        '@WndDirCardinal': windDirection
      },
      temperatureFormat,
    } = this.props;

    const temp = (temperatureFormat !== 'C') ? TempF : TempC;

    return (
      <Row className="HourlyForecastRow ForecastRow">
        <Col xs={3} sm={3} md={2}>
          <div className="time-of-day">
            {formatDate(dateTimeOfForecast, 'hA')}
          </div>
          <div className="date">
            {formatDate(dateTimeOfForecast, 'MM/DD')}
          </div>
        </Col>
        <Col xs={2} sm={2}>
          <span className="ForecastRow-hiTemp" dangerouslySetInnerHTML={{__html: temp + "&deg;"}} />
        </Col>
        <Col xs={2} md={4}>
          <span className={"weather-icon weather-icon-small-" + iconCode}/>
          <span className="weather-description">{descriptionText}</span>
        </Col>
        <Col xs={2} sm={2} lg={1}>
          <span className="ForecastRow-precip weather-percentage weather-precipitation">{precipitation}</span>
        </Col>
        <Col xs={2} sm={2} lg={1}>
          <span className="ForecastRow-humidity weather-percentage">{humidity}</span>
        </Col>
        <Col xsHidden smHidden mdHidden lg={2}>
          <span className="ForecastRow-wind weather-wind">{windDirection + " " + windSpeed + " MPH"}</span>
        </Col>
      </Row>
    );
  }
}

/**
 * Class representing the multiday weather forecast component.
 */
class MultiDayForecast extends Component {
  static propTypes = {
    forecasts: PropTypes.array.isRequired,
    daysToDisplay: PropTypes.string,
    temperatureFormat: PropTypes.string,
    hiFirst: PropTypes.bool,
    expandFirstRowByDefault: PropTypes.bool
  };

  render() {
    const {
      forecasts,
      daysToDisplay,
      hiFirst,
      expandFirstRowByDefault,
      lang = 'en',
    } = this.props;

    return (
      <div>
        <MultiDayForecastHeadings lang={lang} />
        <MultiDayForecastBody
          forecasts={forecasts}
          daysToDisplay={daysToDisplay}
          temperatureFormat={this.props.temperatureFormat}
          hiFirst={hiFirst}
          expandFirstRowByDefault={expandFirstRowByDefault}
        />
      </div>
    );
  }
}

/**
 * Class representing the headings panel for the multiday weather forecast component.
 */
class MultiDayForecastHeadings extends Component {
  render() {
    const { lang = 'en' } = this.props;
    return (
      <Row className="weather-multiday-hourly-headings">
        <Col xs={3} sm={2} className="multiday-hourly-heading">{translate('Day', lang)}</Col>
        <Col xs={2} sm={2} className="multiday-hourly-heading">Temp</Col>
        <Col xs={2} sm={4} className="multiday-hourly-heading">
          <span className="long-name">{translate('Description', lang)}</span>
          <span className="short-name">Desc</span>
        </Col>
        <Col xs={2} sm={2} lg={1} className="multiday-hourly-heading">
          Precip
        </Col>
        <Col xs={2} sm={2} lg={1} className="multiday-hourly-heading">
          <span className="long-name">{translate('Humidity', lang)}</span>
          <span className="short-name">{translate('Humid', lang)}</span>
        </Col>
        <Col xsHidden smHidden mdHidden lg={2} className="multiday-hourly-heading">{translate('Wind', lang)}</Col>
      </Row>
    );
  }
}

/**
 * Class representing the container for the content within the multiday weather forecast component.
 */
class MultiDayForecastBody extends Component {
  static propTypes = {
    forecasts: PropTypes.array.isRequired,
    daysToDisplay: PropTypes.string,
    temperatureFormat: PropTypes.string,
    hiFirst: PropTypes.bool,
    expandFirstRowByDefault: PropTypes.bool
  };

  state = {
    expandedRowIndex: this.props.expandFirstRowByDefault ? 0 : -1
  }

  componentWillReceiveProps(nextProps) {
    const { 'daysToDisplay': oldDaysToDisplay, expandFirstRowByDefault } = nextProps;
    const { newDaysToDisplay } = nextProps;
    const expandedIndex = expandFirstRowByDefault ? 0 : -1;

    if (oldDaysToDisplay !== newDaysToDisplay) {
      this.setState({
        expandedRowIndex: expandedIndex
      });
    }
  }

  // get first weekend from daily forecast
  _getFirstWeekend(forecasts) {
    const weekend = [];

    for (const forecast of forecasts) {
      const date = new Date(forecast['@ValidDateLocal']);
      // Friday: 5, Saturday: 6, Sunday: 0
      if (date.getDay() === 5 || date.getDay() === 6 || date.getDay() === 0) {
        weekend.push(forecast);

        if (date.getDay() === 0){
          return weekend;
        }
      }
    }
    return weekend;
  }

  _setExpandedRowIndex(value = -1){
    this.setState({
      expandedRowIndex: value
    });
  }

  render() {
    const { forecasts, daysToDisplay, temperatureFormat, hiFirst } = this.props;
    const { expandedRowIndex } = this.state;
    const emptyRowIndex = -1;

    const totalDaysToDisplay = daysToDisplay;
    let elementsToDisplay;
    let dailyElements;

    if (forecasts) {
      if (totalDaysToDisplay === 'weekend') {
        elementsToDisplay = this._getFirstWeekend(forecasts);
      } else {
        elementsToDisplay = take(forecasts, Number(totalDaysToDisplay));
      }

      dailyElements = map(elementsToDisplay, (forecast, i) => {
        return (<MultiDayForecastRow
          key={i}
          forecast={forecast}
          temperatureFormat={temperatureFormat}
          isExpanded={i === expandedRowIndex}
          rowIndex={i}
          onExpand={this._setExpandedRowIndex.bind(this)}
          onCollapse={this._setExpandedRowIndex.bind(this, emptyRowIndex)}
          hiFirst={hiFirst}
        />);
      });
    }

    return (
      <div>
        {dailyElements}
      </div>
    );
  }
}

/**
 * Class representing a row that contains information about a single day within the multiday weather forecast component.
 */
class MultiDayForecastRow extends Component {
  static propTypes = {
    forecast: PropTypes.object.isRequired,
    temperatureFormat: PropTypes.string,
    isExpanded: PropTypes.bool,
    rowIndex: PropTypes.number,
    onExpand: PropTypes.func,
    onCollapse: PropTypes.func,
    hiFirst: PropTypes.bool
  };

  static contextTypes = {
    config: PropTypes.object.isRequired,
  }

  constructor(props) {
    super(props);

    //Give internal functions correct context
    this._onExpandClick = this._onExpandClick.bind(this);
    this._onCollapseClick = this._onCollapseClick.bind(this);
    this._onSummaryChange = this._onSummaryChange.bind(this);
    this._onOverviewChange = this._onOverviewChange.bind(this);
    this._onRowClick = this._onRowClick.bind(this);
  }

  state = {
    showSummary: !this.props.isExpanded,
    showExpanded: this.props.isExpanded
  };

  componentWillMount() {
    this.setState({
      isRowExpanded: false
    });
  }

  componentWillReceiveProps(nextProps) {
    const { isExpanded } = nextProps;
    const newState = {
      isRowExpanded: isExpanded
    };

    if (isExpanded) {
      newState['showSummary'] = false;
    } else {
      newState['showExpanded'] = false;
    }

    this.setState(newState);
  }

  _onRowClick() {
    const { isExpanded, rowIndex, onExpand, onCollapse } = this.props;

    if (isExpanded) {
      onCollapse();
    } else {
      onExpand(rowIndex);
    }
  }

  _onExpandClick() {
     const { onExpand, rowIndex } = this.props;

    onExpand(rowIndex);
  }

  _onCollapseClick() {
     const { onCollapse } = this.props;

    onCollapse();
  }

  _onSummaryChange(component) {
    const { showSummary, isRowExpanded } = this.state;

    if (!component && !showSummary){
      this.setState({
        showExpanded: isRowExpanded,
        showSummary: !isRowExpanded
      });
    }
  }

  _onOverviewChange(component) {
    if (!component && !this.state.showSummary){
      this.setState({
        showSummary: true
      });
    }
  }

  render() {
    const {
      config: {
        appOptions: {
          language
        } = {}
      } = {}
    } = this.context;

    const {
      hiFirst,
      temperatureFormat,
      forecast
    } = this.props;
    const {
      showExpanded,
      showSummary
    } = this.state;

    return (
      <Row className="MultiDayForecastRow ForecastRow" onClick={this._onRowClick}>
        <ReactCSSTransitionGroup transitionName="weather-daily-summary" transitionEnterTimeout={200} transitionLeaveTimeout={200}>
          {showExpanded ?
            <MultiDayForecastDailyExpanded
              key={1}
              ref={this._onOverviewChange}
              temperatureFormat={temperatureFormat}
              dailyForecast={forecast}
              lang={language}
            /> :
            null}
          {showSummary ?
            <MultiDayForecastDailySummary
              key={2}
              ref={this._onSummaryChange}
              hiFirst={hiFirst}
              temperatureFormat={temperatureFormat}
              dailyForecast={forecast}
            /> : null}
        </ReactCSSTransitionGroup>
        <DownArrow
          arrowState={showSummary ? "arrow" : "cross"}
          onOpenClick={this._onExpandClick}
          onCloseClick={this._onCollapseClick}
          />
      </Row>
    );
  }
}

/**
 * Class representing summarised information about a single day within the multiday weather forecast component.
 */
class MultiDayForecastDailySummary extends Component {
  static propTypes = {
    dailyForecast: PropTypes.object.isRequired,
    temperatureFormat: PropTypes.string,
    hiFirst: PropTypes.bool
  };

  render() {
    const { dailyForecast, hiFirst } = this.props;
    const {
      '@DayOfWk': dayOfWeek,
      '@HiTempF': hiTempF,
      '@LoTempF': loTempF,
      '@HiTempC': hiTempC,
      '@LoTempC': loTempC,
      '@ValidDateLocal': date,
      '@IconCode': iconCode,
      '@SkyText': descriptionText,
      '@PrecipChance': precipitation,
      '@RelHumidity': humidity,
      '@WndSpdMph': windSpeed,
      '@WndDirCardinal': windDirection
      } = dailyForecast;

    // Use the correct unit (celsius vs fahrenheit)
    const highTemp = (this.props.temperatureFormat !== "C") ? hiTempF : hiTempC;
    const lowTemp = (this.props.temperatureFormat !== "C") ? loTempF : loTempC;

    return (
      <div className="daily-forecast-summary">
        <Col xs={3} sm={2}>
          <div className="day-of-week">
            {dayOfWeek.toUpperCase().substring(0, 3)}
          </div>
          <div className="date">
            {formatDate(date, 'MM/DD')}
          </div>
        </Col>
        <Col xs={2} sm={2}>
          {hiFirst ?
            <span className="ForecastRow-hiTemp" dangerouslySetInnerHTML={{__html: highTemp + "&deg;"}}/>
            : <span className="MultiDayForecastRow-loTemp" dangerouslySetInnerHTML={{__html: lowTemp + "&deg;"}} /> }
          <span className="weather-slash" />
          {hiFirst ?
            <span className="MultiDayForecastRow-loTemp" dangerouslySetInnerHTML={{__html: lowTemp + "&deg;"}} />
            : <span className="ForecastRow-hiTemp" dangerouslySetInnerHTML={{__html: highTemp + "&deg;"}}/> }
        </Col>
        <Col xs={2} sm={4}>
          <span className={"weather-icon weather-icon-small-" + iconCode}/>
          <span className="weather-description">{descriptionText}</span>
        </Col>
        <Col xs={2} sm={2} lg={1}>
          <span className="ForecastRow-precip weather-percentage weather-precipitation">{precipitation}</span>
        </Col>
        <Col xs={2} sm={2} lg={1}>
          <span className="ForecastRow-humidity weather-percentage">{humidity}</span>
        </Col>
        <Col xsHidden smHidden mdHidden lg={2}>
          <span className="ForecastRow-wind">{windDirection + " " + windSpeed + " MPH"}</span>
        </Col>
      </div>
    );
  }
}

/**
 * Class representing verbose information about a single day within the multiday weather forecast component.
 */
class MultiDayForecastDailyExpanded extends Component {
  static propTypes = {
    dailyForecast: PropTypes.object.isRequired,
    temperatureFormat: PropTypes.string
  };

  render() {
    const { dailyForecast, lang = 'en' } = this.props;
    const {
      '@DayOfWk': dayOfWeek,
      '@ValidDateLocal': dateTime,
      '@IconCode': iconCodeDay,
      '@HiTempF': hiTempF,
      '@HiTempC': hiTempC,
      '@SkyText': descriptionTextDay,
      '@PhraseDay': weatherPhraseDay,
      '@Sunrise': sunrise,
      '@Sunset': sunset,
      '@UvIdx': uvIndex,
      '@UvDescr': uvDescription,
      '@PrecipChanceDay': precipitationDay,
      '@RelHumidity': humidityDay,
      '@WndSpdMph': windSpeedDay,
      '@WndDirCardinal': windDirectionDay,
      '@LoTempF': loTempF,
      '@LoTempC': loTempC,
      '@IconCodeNight': iconCodeNight,
      '@SkyTextNight': descriptionTextNight,
      '@PhraseNight': weatherPhraseNight,
      '@Moonrise': moonrise,
      '@Moonset': moonset,
      '@MoonPhaseText': moonPhase,
      '@PrecipChanceNight': precipitationNight,
      '@RelHumidityNight': humidityNight,
      '@WndSpdMphNight': windSpeedNight,
      '@WndDirCardinalNight': windDirectionNight
      } = dailyForecast;

    // Use the correct unit (celsius vs fahrenheit)
    const highTemp = (this.props.temperatureFormat !== "C") ? hiTempF : hiTempC;
    const lowTemp = (this.props.temperatureFormat !== "C") ? loTempF : loTempC;

    const dateOnly = dateTime.split(" ")[0];

    return (
      <div className="MultiDayForecastDailyExpanded daily-forecast-summary daily-forecast-summary-expanded">
        <Col xs={12} sm={12} md={6} lg={6}>
          <div className="day-of-week">
            {dayOfWeek.toUpperCase()}
          </div>
          <div className="date">
            {formatDate(dateTime, 'MM/DD')}
          </div>
          <div className="temp-description">
            <span className={"weather-icon weather-icon-" + iconCodeDay}/>
            <span className="temperature" dangerouslySetInnerHTML={{__html: highTemp + "&deg;"}} />
            <span className="weather-description">{descriptionTextDay.toUpperCase()}</span>
          </div>
          <div className="weather-phrase">
          {weatherPhraseDay || ''}
          </div>
          <Row className="MultiDayForecastDailyExpanded-details">
            {sunrise ?
              <Col xs={6} sm={4} className="MultiDayForecastDailyExpanded-sunrise">
                <div className="weather-title">{translate('Sunrise', lang)}</div>
                <div className="weather-sun sunrise">{formatDate(dateOnly + ' ' + sunrise, 'h:mm A')}</div>
              </Col>
              : null
            }
            {sunset ?
              <Col xs={6} sm={4} className="MultiDayForecastDailyExpanded-sunset">
                <div className="weather-title">{translate('Sunset', lang)}</div>
                <div className="weather-sun sunset">{formatDate(dateOnly + ' ' + sunset, 'h:mm A')}</div>
              </Col>
              : null
            }
            {uvIndex ?
              <Col xs={6} sm={4} className="MultiDayForecastDailyExpanded-uv">
                <div className="weather-title">{translate('UV Index', lang)}</div>
                <div className="uv-index">{uvIndex + " " + uvDescription.toUpperCase()}</div>
              </Col>
              : null
            }
            {windSpeedDay ?
              <Col xs={6} sm={4} className="MultiDayForecastDailyExpanded-wind">
                <div className="weather-title">{translate('Wind', lang)}</div>
                <div className="weather-wind">{windDirectionDay + " " + windSpeedDay + " MPH"}</div>
              </Col>
              : null
            }
            {humidityDay ?
              <Col xs={6} sm={4} className="MultiDayForecastDailyExpanded-humidity">
                <div className="weather-title">{translate('Humidity', lang)}</div>
                <div className="weather-percentage">{humidityDay}</div>
              </Col>
              : null
            }
            {precipitationDay ?
              <Col xs={6} sm={4} className="MultiDayForecastDailyExpanded-precipitation">
                <div className="weather-title">{translate('Precip.', lang)}</div>
                <div className="weather-percentage weather-precipitation">{precipitationDay}</div>
              </Col>
              : null
            }
          </Row>
        </Col>
        <Col xs={12} sm={12} md={6} lg={6}>
          <div className="day-of-week">
            {`${dayOfWeek.toUpperCase()} ${translate('NIGHT', lang)}`}
          </div>
          <div className="temp-description">
            <span className={"weather-icon weather-icon-" + iconCodeNight}/>
            <span className="temperature" dangerouslySetInnerHTML={{__html: lowTemp + "&deg;"}} />
            <span className="weather-description">{descriptionTextNight.toUpperCase()}</span>
          </div>
          <div className="weather-phrase">
            {weatherPhraseNight || ''}
          </div>
          <Row className="MultiDayForecastDailyExpanded-details">
            {moonrise ?
              <Col xs={6} sm={4} className="MultiDayForecastDailyExpanded-moonrise">
                <div className="weather-title">{translate('Moonrise', lang)}</div>
                <div className="weather-moon moonrise">{formatDate(dateOnly + ' ' + moonrise, 'h:mm A')}</div>
              </Col>
              : null
            }
            {moonset ?
              <Col xs={6} sm={4} className="MultiDayForecastDailyExpanded-moonset">
                <div className="weather-title">{translate('Moonset', lang)}</div>
                <div className="weather-moon moonset">{formatDate(dateOnly + ' ' + moonset, 'h:mm A')}</div>
              </Col>
              : null
            }
            {moonPhase ?
              <Col xs={6} sm={4} className="MultiDayForecastDailyExpanded-moonPhase">
                <div className="weather-title">{translate('Moon Phase', lang)}</div>
                <div className="moon-phase">{moonPhase}</div>
              </Col>
              : null
            }
            {windSpeedNight ?
              <Col xs={6} sm={4} className="MultiDayForecastDailyExpanded-wind">
                <div className="weather-title">{translate('Wind', lang)}</div>
                <div className="weather-wind">{windDirectionNight + " " + windSpeedNight + " MPH"}</div>
              </Col>
              : null
            }
            {humidityNight ?
              <Col xs={6} sm={4} className="MultiDayForecastDailyExpanded-humidity">
                <div className="weather-title">{translate('Humidity', lang)}</div>
                <div className="weather-percentage">{humidityNight}</div>
              </Col>
              : null
            }
            {precipitationNight ?
              <Col xs={6} sm={4} className="MultiDayForecastDailyExpanded-precipitation">
                <div className="weather-title">{translate('Precip.', lang)}</div>
                <div className="weather-percentage weather-precipitation">{precipitationNight}</div>
              </Col>
            : null
            }
          </Row>
        </Col>
      </div>
    );
  }
}

export default DetailedWeatherBreakdown;



// WEBPACK FOOTER //
// ./app/components/weather/DetailedWeatherBreakdown.js